import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { LinkItem } from "LinkItem";
import { OLButton } from "OLButton";
import { getBackendErrors, getErrorMessage, getPlainError } from "api/errors";
import { useSettings } from "api/general";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { TextInput } from "components/FormControls/TextInput";
import { I18nContext } from "contexts/I18n/I18nContext";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { Form, Formik } from "formik";
import { useMessage } from "hooks/intl";
import { useContext, useState } from "react";
import { backendErrors } from "utilities/backendErrors";
import { focusError } from "utilities/focusError";
import { links } from "utilities/links";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    paper: {
      backgroundColor: theme.palette.background.paper,
      border: "none",
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
      maxWidth: "95%",
      padding: theme.spacing(12, 12, 16),
    },
    header: {
      color: theme.black,
      fontSize: 20,
      fontWeight: 500,
      lineHeight: "32px",
      marginBottom: theme.spacing(6),
    },
    grayTextSmall: {
      color: theme.gray500,
      display: "block",
      fontSize: 16,
      marginBottom: theme.spacing(12),
    },
    Input: {
      maxWidth: "100%",
      width: 406,
      "& + &": {
        marginTop: theme.spacing(10),
      },
    },
    submit: {
      height: theme.spacing(24),
      marginTop: theme.spacing(12),
    },
    form: {
      display: "flex",
      flexDirection: "column",
    },
    error: {
      color: theme.red500,
      display: "block",
      fontSize: 14,
      marginBottom: theme.spacing(8),
      marginTop: theme.spacing(5),
      "& + &": { marginTop: 0 },
    },
    link: {
      textAlign: "center",
      textTransform: "uppercase",
      fontWeight: 500,
      fontSize: 15,
    },
  }),
);

export function PasswordRecovery() {
  const classes = useStyles();
  const { notify } = useNotifications();
  const { message } = useMessage();
  const { requestPasswordChange } = useSettings();
  const [instructionsSent, setInstructionsSent] = useState(false);
  const { refetchLanguages } = useContext(I18nContext);

  refetchLanguages();

  return (
    <>
      <div className={classes.paper}>
        <h1 className={classes.header}>{message({ id: "forgotPassword" })}</h1>
        <span className={classes.grayTextSmall}>
          {message({
            id: `${
              instructionsSent ? "passwordSentInfo" : "forgotPasswordInfo"
            }`,
          })}
        </span>
        {!instructionsSent && (
          <Formik
            initialStatus={{}}
            initialValues={{ username: "" }}
            onSubmit={async (values, { setSubmitting, setStatus }) => {
              setSubmitting(true);
              setStatus({});
              try {
                await requestPasswordChange(values.username);
                setInstructionsSent(true);
                notify(message({ id: "passwordSent" }));
              } catch (exception) {
                setSubmitting(false);
                notify(
                  getErrorMessage(exception) ||
                    (getPlainError(exception) as string),
                );
                const errors = backendErrors(getBackendErrors(exception));
                setStatus(errors);
                focusError();
              }
            }}
          >
            {({ handleSubmit, isSubmitting, status }) => {
              return (
                <Form onSubmit={handleSubmit} className={classes.form}>
                  {status?.[""] && (
                    <span className={classes.error}>{status[""]}</span>
                  )}
                  <TextInput
                    className={classes.Input}
                    label={
                      <RequiredFieldLabel text={message({ id: "username" })} />
                    }
                    name="username"
                    data-test-id="password-recovery-username-input"
                  />
                  <OLButton
                    className={classes.submit}
                    color="primary"
                    disabled={isSubmitting}
                    fullWidth
                    type="submit"
                    variant="contained"
                    data-test-id="password-recovery-recover-password-button"
                    size="large"
                  >
                    {message({ id: "recoverPassword" })}
                  </OLButton>
                </Form>
              );
            }}
          </Formik>
        )}
        <LinkItem
          blue
          to={links.homepage}
          data-test-id="password-recovery-back-to-login-page-link"
          className={classes.link}
        >
          {message({ id: "backToLoginPage" })}
        </LinkItem>
      </div>
    </>
  );
}
